import { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Crown, Trophy, TrendingUp, Timer, Sparkles, X } from 'lucide-react';
import { motion } from 'framer-motion';

interface JackpotWin {
  id: string;
  jackpot_tier: string;
  currency_type: string;
  amount: number;
  created_at: string;
}

interface JackpotWinModalProps {
  win: JackpotWin | null;
  onClose: () => void;
}

const tierIcons = {
  JACKPOT: Crown,
  GRAND: Trophy, 
  MAJOR: TrendingUp,
  MINI: Timer
};

const tierConfig = {
  JACKPOT: {
    color: 'hsl(var(--casino-purple))',
    glowColor: 'hsl(var(--casino-purple) / 0.3)',
    btnGlow: 'hsl(var(--casino-purple) / 0.5)',
    label: 'Jackpot',
  },
  GRAND: {
    color: 'hsl(var(--casino-gold))',
    glowColor: 'hsl(var(--casino-gold) / 0.3)',
    btnGlow: 'hsl(var(--casino-gold) / 0.5)',
    label: 'Grand',
  },
  MAJOR: {
    color: 'hsl(var(--casino-green))',
    glowColor: 'hsl(var(--casino-green) / 0.3)',
    btnGlow: 'hsl(var(--casino-green) / 0.5)',
    label: 'Major',
  },
  MINI: {
    color: 'hsl(var(--casino-blue))',
    glowColor: 'hsl(var(--casino-blue) / 0.3)',
    btnGlow: 'hsl(var(--casino-blue) / 0.5)',
    label: 'Mini',
  }
};

export const JackpotWinModal = ({ win, onClose }: JackpotWinModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [displayedWin, setDisplayedWin] = useState<JackpotWin | null>(null);
  const [winQueue, setWinQueue] = useState<JackpotWin[]>([]);
  const processedWinsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (win && !processedWinsRef.current.has(win.id)) {
      processedWinsRef.current.add(win.id);
      setWinQueue(prev => {
        if (prev.some(w => w.id === win.id)) return prev;
        return [...prev, win];
      });
    }
  }, [win]);

  useEffect(() => {
    if (winQueue.length > 0 && !isOpen) {
      const nextWin = winQueue[0];
      setDisplayedWin(nextWin);
      setIsOpen(true);
      setWinQueue(prev => prev.slice(1));
    }
  }, [winQueue, isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setDisplayedWin(null);
      onClose();
    }, 300);
  };

  if (!displayedWin) return null;

  const displayTier = displayedWin.jackpot_tier.replace(/_SC$|_GC$/, '');
  const Icon = tierIcons[displayTier as keyof typeof tierIcons] || Crown;
  const config = tierConfig[displayTier as keyof typeof tierConfig] || tierConfig.JACKPOT;

  const formatAmount = (amount: number, currency: string) => {
    if (currency === 'GC') {
      return amount.toLocaleString(undefined, { maximumFractionDigits: 0 });
    } else {
      return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-md w-[95vw] p-0 overflow-hidden border-0 bg-transparent shadow-none [&>button]:hidden"
        aria-describedby={undefined}
      >
        <div 
          className="relative rounded-2xl overflow-hidden bg-card"
          style={{
            boxShadow: `0 0 60px ${config.glowColor}, 0 25px 50px hsl(var(--background) / 0.5)`,
            border: `1px solid ${config.color}`,
            borderColor: `color-mix(in srgb, ${config.color} 40%, transparent)`,
          }}
        >
          {/* Decorative elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div 
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[200%] h-[50%]"
              style={{
                background: `radial-gradient(ellipse at center, ${config.glowColor}, transparent)`,
              }}
            />
            <Sparkles className="absolute top-6 left-8 h-5 w-5 animate-pulse" style={{ color: config.color, opacity: 0.5 }} />
            <Sparkles className="absolute top-10 right-10 h-4 w-4 animate-pulse" style={{ color: config.color, opacity: 0.4, animationDelay: '0.3s' }} />
            <Sparkles className="absolute bottom-24 left-10 h-4 w-4 animate-pulse" style={{ color: config.color, opacity: 0.25, animationDelay: '0.6s' }} />
            <Sparkles className="absolute bottom-20 right-8 h-5 w-5 animate-pulse" style={{ color: config.color, opacity: 0.35, animationDelay: '0.9s' }} />
          </div>

          {/* Top accent border */}
          <div 
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background: `linear-gradient(to right, transparent, ${config.color}, transparent)`,
              opacity: 0.4,
            }}
          />

          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-50 w-9 h-9 rounded-full bg-muted/80 hover:bg-muted flex items-center justify-center transition-colors border border-border"
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>

          {/* Content */}
          <div className="relative z-10 p-6 sm:p-8">
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="text-center space-y-6"
            >
              <DialogHeader className="space-y-4">
                {/* Icon */}
                <motion.div
                  initial={{ y: -20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="flex justify-center"
                >
                  <motion.div
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    className="p-4 rounded-full"
                    style={{
                      background: `linear-gradient(135deg, color-mix(in srgb, ${config.color} 20%, transparent), color-mix(in srgb, ${config.color} 8%, transparent))`,
                      boxShadow: `0 0 30px ${config.glowColor}`,
                      border: `2px solid color-mix(in srgb, ${config.color} 35%, transparent)`,
                    }}
                  >
                    <Icon className="h-12 w-12" style={{ color: config.color }} />
                  </motion.div>
                </motion.div>
                
                {/* Title */}
                <motion.div
                  initial={{ y: 10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.3, duration: 0.4 }}
                >
                  <DialogTitle className="text-2xl sm:text-3xl font-bold text-center text-foreground">
                    Jackpot Won
                  </DialogTitle>
                </motion.div>
                
                {/* Subtitle */}
                <DialogDescription className="text-center space-y-2">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.4, duration: 0.5, type: "spring" }}
                    className="text-lg font-medium text-foreground/80"
                  >
                    You won the <span style={{ color: config.color }} className="font-semibold">{config.label}</span> jackpot
                  </motion.div>
                </DialogDescription>
              </DialogHeader>

              {/* Amount Display */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5, type: "spring" }}
                className="py-6"
              >
                <div 
                  className="text-4xl sm:text-5xl font-bold mb-2"
                  style={{ color: config.color }}
                >
                  {formatAmount(displayedWin.amount, displayedWin.currency_type)} {displayedWin.currency_type}
                </div>
                <div className="text-sm text-muted-foreground">
                  Your balance has been updated
                </div>
              </motion.div>

              {/* Action Button */}
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.5 }}
              >
                <Button 
                  onClick={handleClose}
                  className="w-full h-12 font-bold text-base rounded-xl transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
                  style={{
                    background: `linear-gradient(135deg, ${config.color}, color-mix(in srgb, ${config.color} 80%, black))`,
                    color: 'white',
                    boxShadow: `0 4px 20px ${config.btnGlow}, 0 2px 8px hsl(var(--background) / 0.3)`,
                    border: `1px solid color-mix(in srgb, ${config.color} 60%, white)`,
                  }}
                  size="lg"
                >
                  Continue Playing
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
